import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  Heart,
  Shield,
  Clock,
  Users,
  Star,
  PhoneCall,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1765896387377-e293914d1e69?w=1920&q=80&auto=format',
  service1: 'https://images.pexels.com/photos/7345474/pexels-photo-7345474.jpeg?w=800&auto=compress',
  service2: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=800&q=80&auto=format',
  service3: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=800&q=80&auto=format',
};

const Home = () => {
  const services = [
    {
      icon: <Heart size={32} />,
      title: 'Permanent PCA Placements',
      image: IMAGES.service1,
      description: 'We connect aged care homes with qualified, compassionate Personal Care Assistants for permanent positions. Every placement is made with care to ensure the right fit for your facility.',
    },
    {
      icon: <Users size={32} />,
      title: 'Dedicated Aged Care Staff',
      image: IMAGES.service2,
      description: 'Our PCAs are experienced in supporting elderly residents with daily living, personal hygiene, mobility and companionship in residential aged care settings.',
    },
    {
      icon: <Shield size={32} />,
      title: 'Compliance and Screening',
      image: IMAGES.service3,
      description: 'Every candidate undergoes thorough background checks, police clearances, NDIS Worker Screening and reference verification before placement.',
    },
  ];

  const steps = [
    { num: '01', title: 'Tell us your needs', desc: 'Share your staffing requirements — roles, shifts and facility details.' },
    { num: '02', title: 'We source and screen', desc: 'We find the right candidates, complete all checks and shortlist the best fit.' },
    { num: '03', title: 'Your staff are placed', desc: 'Your new PCA starts — supported by PrimeShift every step of the way.' },
  ];

  const faqs = [
    { question: 'What areas of Sydney do you cover?', answer: 'We provide PCA placements across the entire Sydney metropolitan area and surrounding regions.' },
    { question: 'Are all staff background checked?', answer: 'Yes. Every candidate undergoes police clearance, NDIS Worker Screening, reference checks and identity verification.' },
    { question: 'How quickly can you fill a position?', answer: 'We respond to all enquiries within 24 hours and can often place qualified staff within days depending on requirements.' },
    { question: 'Do you provide permanent staff only?', answer: 'Our primary focus is permanent PCA placements for aged care facilities, ensuring long-term stability for your team and residents.' },
  ];

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative bg-care-blue text-white" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.hero} alt="Caring for elderly" className="w-full h-full object-cover opacity-25" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-32 md:py-40">
          <div className="max-w-4xl">
            <p className="text-care-green font-semibold text-lg mb-3 tracking-wide">AGED CARE DIVISION</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight" data-testid="hero-headline">
              PrimeShift Group
            </h1>
            <p className="text-2xl md:text-3xl font-light text-white/90 mb-8">
              Prime Staff for Prime Aged Care Homes in Sydney
            </p>
            <p className="text-lg text-gray-200 mb-10 leading-relaxed max-w-2xl">
              We provide high-quality Personal Care Assistants for permanent positions in aged care homes and facilities across Sydney.
            </p>
            <p className="text-sm text-white/60 mb-10 tracking-wide">
              Fully insured | All checks completed | Fast permanent placements across Sydney aged care homes
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/clients">
                <Button size="lg" className="w-full sm:w-auto bg-care-green hover:bg-care-green-rich text-white text-lg px-10 py-7 font-semibold transition-colors duration-300">
                  Request PCAs <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/careers">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-care-blue text-lg px-10 py-7 font-semibold transition-colors duration-300">
                  Apply as a PCA
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About snippet */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-care-dark mb-6">About PrimeShift Group</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            PrimeShift Group is a Sydney-based recruitment agency specialising in permanent Personal Care Assistant placements for aged care homes and residential facilities. We are dedicated to connecting aged care providers with compassionate, qualified and reliable care staff.
          </p>
          <Link to="/about">
            <Button variant="outline" className="border-care-blue text-care-blue hover:bg-care-blue hover:text-white transition-colors duration-300">
              Learn More About Us
            </Button>
          </Link>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-care-light" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-care-dark mb-4">Our Services</h2>
            <p className="text-lg text-gray-500">Permanent PCA Placements for Aged Care Homes</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((s, i) => (
              <Card key={i} className="border-0 shadow-sm hover:shadow-lg transition-shadow overflow-hidden bg-white">
                <div className="h-48 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-care-light text-care-blue mb-4">
                    {s.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-care-dark mb-3">{s.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{s.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-care-light">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-care-dark mb-4">How It Works</h2>
            <p className="text-lg text-gray-500">Three simple steps to quality aged care staffing</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {steps.map((s) => (
              <div key={s.num} className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-care-blue text-white text-2xl font-bold mb-6">
                  {s.num}
                </div>
                <h3 className="text-xl font-semibold text-care-dark mb-3">{s.title}</h3>
                <p className="text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose PrimeShift Group */}
      <section className="py-20 bg-white" data-testid="why-choose-primeshift-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-care-dark mb-4">Why Choose PrimeShift Group</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Shield size={24} />, text: 'Fully insured and compliant recruiters' },
              { icon: <CheckCircle size={24} />, text: 'All PCAs are thoroughly screened (Police Check, WWVP, vaccinations, references)' },
              { icon: <Clock size={24} />, text: 'Fast permanent placements — we focus on quality matches' },
              { icon: <Heart size={24} />, text: 'Dedicated support — we stay in touch every step of the way' },
              { icon: <Users size={24} />, text: 'Specialising in permanent PCA roles for aged care homes across Sydney' },
              { icon: <Star size={24} />, text: 'Transparent and professional service' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-care-light rounded-lg p-6">
                <div className="text-care-blue flex-shrink-0 mt-0.5">{item.icon}</div>
                <p className="text-care-dark text-base leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-care-light">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-care-dark mb-12 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-lg font-semibold text-care-dark">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-care-blue text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Need Quality Aged Care Staff?</h2>
          <p className="text-lg text-white/80 mb-10 leading-relaxed">
            Get in touch today. We respond to all enquiries within 24 hours and provide quality PCA placements you can trust.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/clients">
              <Button size="lg" className="w-full sm:w-auto bg-care-green hover:bg-care-green-rich text-white text-lg px-10 py-7 font-semibold transition-colors duration-300">
                Request PCAs <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <a href="tel:+61281889656">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-care-blue text-lg px-10 py-7 font-semibold transition-colors duration-300">
                <PhoneCall className="mr-2" size={20} /> Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
